import { GameState } from '../../core/GameState.js';
import { formatDuration, formatDate } from '../../utils/formatting.js';
import {
  estimateShipEta, estimateDepartureEta, formatShipEta, describeEtaBlocked, NO_ETA_TEXT
} from '../../utils/eta.js';

/**
 * ETA strings / markup for held departures and the route-selection preview
 * (Feedback #53). Kept out of FleetPanel, PortMarker and UIManager.
 */

const HOLD_SHOWN_HOURS = 0.5;

function _estimate(ship) {
  return estimateShipEta(ship, { now: GameState.currentDate, tradeRoutes: GameState.tradeRoutes });
}

/** True when the ship is in port with a queued departure (held, or waiting for orders). */
export function hasHeldDeparture(ship) {
  return !!ship && ship.status !== 'sailing' && !!ship.pendingDeparture;
}

/** Card chip for a held ship: "ETA ..." or "No ETA - awaiting orders" with the reason as tooltip. '' otherwise. */
export function heldEtaChipHtml(ship) {
  if (!hasHeldDeparture(ship)) return '';
  const result = _estimate(ship);
  if (result.blockedReason) {
    const why = describeEtaBlocked(result.blockedReason);
    return `<span class="ship-eta ship-eta--none"${why ? ` title="${why}"` : ''}>${NO_ETA_TEXT}</span>`;
  }
  return `<span class="ship-eta">ETA ${formatShipEta(result)}</span>`;
}

/** Details-view "ETA" table row for a held ship; '' otherwise. */
export function heldEtaRowHtml(ship) {
  if (!hasHeldDeparture(ship)) return '';
  const result = _estimate(ship);
  if (result.blockedReason) {
    const why = describeEtaBlocked(result.blockedReason);
    return `<tr><td>ETA</td><td${why ? ` title="${why}"` : ''}>${NO_ETA_TEXT}</td></tr>`;
  }
  return `<tr><td>ETA</td><td>${formatShipEta(result)}</td></tr>`;
}

/**
 * Sort key for the ETA order: game hours until arrival. -Infinity for ships
 * with no destination (first), Infinity when the ETA is unknown (last).
 */
export function etaSortHours(ship) {
  if (ship.status !== 'sailing' && !ship.pendingDeparture) return -Infinity;
  const result = _estimate(ship);
  if (result.blockedReason === 'no-destination') return -Infinity;
  if (result.blockedReason || !result.etaDate) return Infinity;
  return (result.etaDate.getTime() - GameState.currentDate.getTime()) / 3600000;
}

/**
 * Route-selection preview text for sailing `distanceNm` from the ship's
 * current situation: "Departs in 3 hrs - ETA 2 days (Sep 04...)". Includes the
 * remaining port time for a docked/held ship. Returns plain text, or null
 * when there is no distance. Blocked results explain why.
 */
export function previewEtaText(ship, distanceNm) {
  const r = estimateDepartureEta(ship, distanceNm, { now: GameState.currentDate });
  if (r.blockedReason === 'no-route-data') return null;
  if (r.blockedReason) {
    const why = describeEtaBlocked(r.blockedReason);
    return `${NO_ETA_TEXT}${why ? ` (${why})` : ''}`;
  }
  const total = `${formatDuration((r.holdHours + r.travelHours) / 24)} (${formatDate(r.etaDate)})`;
  if (r.holdHours < HOLD_SHOWN_HOURS) return total;
  return `Departs in ${formatDuration(r.holdHours / 24)} - ETA ${total}`;
}
